import React, { useState } from 'react';
import { X, Bell, Phone, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

function SetReminderModal({ isOpen, onClose, vehicle }) {
  const [phone, setPhone] = useState('');
  const [insuranceDate, setInsuranceDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error('Please enter your WhatsApp number');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reminders/whatsapp/set`, {
        phone_number: phone,
        registration: vehicle.registration,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        mot_expiry_date: vehicle.mot_expiry_date,
        tax_expiry_date: vehicle.tax_due_date,
        insurance_renewal_date: insuranceDate || null,
      });
      setSuccess(true);
      toast.success('Reminders set!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set reminder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="reminder-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Bell className="h-5 w-5 text-yellow-500" />
            Set Reminders
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="bg-emerald-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-white font-semibold text-lg mb-2">Reminders Set!</p>
            <p className="text-gray-400 text-sm mb-1">We'll WhatsApp you at 30, 14 and 7 days before:</p>
            <div className="text-left bg-gray-800 rounded-lg p-4 mt-3 space-y-2">
              {vehicle.mot_expiry_date && (
                <p className="text-gray-300 text-sm">MOT: {vehicle.mot_expiry_date}</p>
              )}
              {vehicle.tax_due_date && (
                <p className="text-gray-300 text-sm">Tax: {vehicle.tax_due_date}</p>
              )}
              {insuranceDate && (
                <p className="text-gray-300 text-sm">Insurance: {insuranceDate}</p>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-3">Reply STOP anytime to opt out</p>
            <Button onClick={onClose} className="mt-4 bg-yellow-500 hover:bg-yellow-400 text-gray-900">
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-800 rounded-lg p-3 mb-4">
              <p className="text-yellow-500 font-bold tracking-wider">{vehicle.registration}</p>
              <p className="text-gray-400 text-sm">{vehicle.make} {vehicle.model}</p>
              {vehicle.mot_expiry_date && (
                <p className="text-gray-500 text-xs mt-1">MOT expires: {vehicle.mot_expiry_date}</p>
              )}
              {vehicle.tax_due_date && (
                <p className="text-gray-500 text-xs">Tax expires: {vehicle.tax_due_date}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="text-gray-300 text-sm font-medium mb-1 block">
                <Phone className="h-4 w-4 inline mr-1" />
                WhatsApp Number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07700 900000"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
                required
                data-testid="phone-input"
              />
              <p className="text-gray-600 text-xs mt-1">UK mobile number — we'll send via WhatsApp</p>
            </div>

            <div className="mb-5">
              <label className="text-gray-300 text-sm font-medium mb-1 block">
                <Calendar className="h-4 w-4 inline mr-1" />
                Insurance Renewal Date <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="date"
                value={insuranceDate}
                onChange={(e) => setInsuranceDate(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-500 focus:outline-none"
                data-testid="insurance-date-input"
              />
              <p className="text-gray-600 text-xs mt-1">We'll remind you before your insurance is due too</p>
            </div>

            <Button
              type="submit"
              className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold h-12"
              disabled={loading}
              data-testid="set-reminder-submit"
            >
              {loading ? 'Setting up...' : 'Set WhatsApp Reminders'}
            </Button>
            <p className="text-gray-600 text-xs text-center mt-2">No login needed. Reply STOP to opt out anytime.</p>
          </form>
        )}
      </div>
    </div>
  );
}

export default SetReminderModal;
